import { useState, useRef, useEffect, useCallback } from "react";
import "./NetflixPlayer.css";

export default function NetflixPlayer({
  content,
  onClose,
  onProgress,
  onEnded,
  startTime = 0,
  autoPlay = true,
  preferTrailer = false,
}) {
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const hasAppliedStartTimeRef = useRef(false);

  const mainVideo = content.videos?.[0]?.url || content.videos?.[0];
  const trailerVideo = content.netflixData?.trailerUrl;
  const poster =
    content.netflixData?.thumbnail ||
    content.images?.[0]?.url ||
    content.images?.[0];

  const subtitles = content.netflixData?.subtitles || [];
  const audioTracks = content.netflixData?.audioTracks || [];
  const [activeSource, setActiveSource] = useState(
    mainVideo ? (autoPlay ? "main" : "main") : trailerVideo ? "trailer" : "main"
  );
  const [selectedSubtitle, setSelectedSubtitle] = useState("" );
  const [selectedAudio, setSelectedAudio] = useState("");

  const selectedAudioTrack = audioTracks.find((t) => t.language === selectedAudio);
  const sourceUrl = selectedAudioTrack?.url || (activeSource === "trailer" ? trailerVideo : mainVideo || trailerVideo);

  const togglePlay = useCallback(() => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setPlaying((prev) => !prev);
    }
  }, [playing]);

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !muted;
      setMuted((prev) => !prev);
    }
  }, [muted]);

  const applyVolume = useCallback((newVolume) => {
    const safeVolume = Math.min(1, Math.max(0, newVolume));
    setVolume(safeVolume);
    if (videoRef.current) {
      videoRef.current.volume = safeVolume;
      if (safeVolume === 0) {
        setMuted(true);
      } else {
        setMuted(false);
        videoRef.current.muted = false;
      }
    }
  }, []);

  const handleVolumeChange = useCallback((e) => {
    const newVolume = parseFloat(e.target.value);
    applyVolume(newVolume);
  }, [applyVolume]);

  const handleVolumeStep = useCallback((delta) => {
    applyVolume(volume + delta);
  }, [applyVolume, volume]);

  const handleSeek = useCallback((seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, Math.min(duration, currentTime + seconds));
    }
  }, [currentTime, duration]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setFullscreen(true);
    } else {
      document.exitFullscreen();
      setFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        togglePlay();
      } else if (e.code === "ArrowLeft") {
        handleSeek(-10);
      } else if (e.code === "ArrowRight") {
        handleSeek(10);
      } else if (e.code === "ArrowUp") {
        e.preventDefault();
        handleVolumeStep(0.05);
      } else if (e.code === "ArrowDown") {
        e.preventDefault();
        handleVolumeStep(-0.05);
      } else if (e.code === "KeyF") {
        toggleFullscreen();
      } else if (e.code === "KeyM") {
        toggleMute();
      } else if (e.code === "Escape" && onClose) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [togglePlay, handleSeek, handleVolumeStep, toggleFullscreen, toggleMute, onClose]);

  useEffect(() => {
    if (playing) {
      hideControlsAfterDelay();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, currentTime]);

  useEffect(() => () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
      videoRef.current.muted = muted;
    }
  }, [volume, muted]);

  useEffect(() => {
    const tracks = videoRef.current?.textTracks || [];
    for (let i = 0; i < tracks.length; i += 1) {
      tracks[i].mode = tracks[i].language === selectedSubtitle ? "showing" : "disabled";
    }
  }, [selectedSubtitle, content?._id]);

  // Auto apply start time and autoplay when content changes
  useEffect(() => {
    hasAppliedStartTimeRef.current = false;
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setBuffering(false);
    const defaultSource = preferTrailer && trailerVideo ? "trailer" : "main";
    setActiveSource(defaultSource);
    const initialSubtitle = content.netflixData?.defaultSubtitle || subtitles[0]?.language || "";
    setSelectedSubtitle(initialSubtitle);
    const initialAudio = content.netflixData?.defaultAudio || "";
    setSelectedAudio(initialAudio);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content?._id, preferTrailer, trailerVideo, subtitles]);

  const hideControlsAfterDelay = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (playing) setShowControls(false);
    }, 3000);
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (playing) {
      hideControlsAfterDelay();
    }
  };

  const handleProgressClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    if (videoRef.current) {
      videoRef.current.currentTime = pos * duration;
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "0:00";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    }
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      setDuration(videoRef.current.duration);
      if (typeof onProgress === "function") {
        onProgress({
          postId: content._id,
          currentTime: videoRef.current.currentTime,
          duration: videoRef.current.duration,
        });
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (!videoRef.current) return;
    const mediaDuration = videoRef.current.duration || 0;
    setDuration(mediaDuration);

    if (!hasAppliedStartTimeRef.current && startTime > 0 && startTime < mediaDuration) {
      videoRef.current.currentTime = startTime;
      setCurrentTime(startTime);
      hasAppliedStartTimeRef.current = true;
    }

    if (autoPlay) {
      videoRef.current
        .play()
        .then(() => setPlaying(true))
        .catch(() => setPlaying(false));
    }
  };

  const handleEnded = () => {
    setPlaying(false);
    setShowControls(true);
    if (typeof onEnded === "function") {
      onEnded(content);
    }
  };

  return (
    <div 
      ref={containerRef}
      className={`netflix_player ${fullscreen ? 'fullscreen' : ''}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => playing && setShowControls(false)}
    >
      <video
        ref={videoRef}
        src={sourceUrl}
        poster={poster}
        onTimeUpdate={handleTimeUpdate}
        onWaiting={() => setBuffering(true)}
        onCanPlay={() => setBuffering(false)}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onClick={togglePlay}
        crossOrigin="anonymous"
      >
        {subtitles.map((track) => (
          <track
            key={track.language}
            kind="subtitles"
            srcLang={track.language}
            label={track.label || track.language}
            src={track.url}
            default={track.language === selectedSubtitle}
          />
        ))}
      </video>

      {/* Close Button */}
      <button className="netflix_player_close" onClick={onClose}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>

      {/* Title */}
      <div className={`netflix_player_title ${showControls ? 'visible' : ''}`}>
        <h2>{content.netflixData?.title || content.text}</h2>
      </div>

      {/* Buffering Spinner */}
      {buffering && (
        <div className="netflix_player_buffering">
          <div className="netflix_spinner"></div>
        </div>
      )}

      {/* Play/Pause Overlay */}
      {!playing && !buffering && (
        <div className="netflix_player_overlay" onClick={togglePlay}>
          <svg width="80" height="80" viewBox="0 0 24 24" fill="white">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
      )}

      {/* Controls */}
      <div className={`netflix_player_controls ${showControls ? 'visible' : ''}`}>
        {/* Progress Bar */}
        <div className="netflix_progress" onClick={handleProgressClick}>
          <div className="netflix_progress_bar">
            <div 
              className="netflix_progress_filled" 
              style={{ width: `${(currentTime / duration) * 100}%` }}
            />
          </div>
        </div>

        {/* Control Buttons */}
        <div className="netflix_controls_bar">
          <div className="netflix_controls_left">
            <button onClick={togglePlay} className="netflix_control_btn">
              {playing ? (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                </svg>
              ) : (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
            </button>

            <button onClick={() => handleSeek(-10)} className="netflix_control_btn">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                <path d="M11.99 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6h-2c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
              </svg>
            </button>

            <button onClick={() => handleSeek(10)} className="netflix_control_btn">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                <path d="M12.01 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/>
              </svg>
            </button>

            <button onClick={toggleMute} className="netflix_control_btn">
              {muted || volume === 0 ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
                </svg>
              )}
            </button>

            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={handleVolumeChange}
              className="netflix_volume_slider"
            />

            <div className="netflix_time">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>

          <div className="netflix_controls_right">
            <button className="netflix_control_btn">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <path d="M21 3H3c-1.11 0-2 .89-2 2v12a2 2 0 0 0 2 2h5v2h8v-2h5c1.1 0 2-.9 2-2V5a2 2 0 0 0-2-2zm0 14H3V5h18v12z"/>
              </svg>
            </button>

            <button onClick={toggleFullscreen} className="netflix_control_btn">
              {fullscreen ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                  <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="netflix_controls_extra">
          {trailerVideo && (
            <div className="netflix_extra_group">
              <span className="netflix_extra_label">Fonte</span>
              <div className="netflix_extra_buttons">
                <button
                  type="button"
                  className={`netflix_chip ${activeSource === "main" ? "active" : ""}`}
                  onClick={() => setActiveSource("main")}
                  disabled={!mainVideo}
                >
                  Principal
                </button>
                <button
                  type="button"
                  className={`netflix_chip ${activeSource === "trailer" ? "active" : ""}`}
                  onClick={() => setActiveSource("trailer")}
                >
                  Trailer
                </button>
              </div>
            </div>
          )}

          {audioTracks.length > 0 && (
            <div className="netflix_extra_group">
              <span className="netflix_extra_label">Áudio</span>
              <select
                className="netflix_extra_select"
                value={selectedAudio}
                onChange={(e) => setSelectedAudio(e.target.value)}
              >
                <option value="">Principal</option>
                {audioTracks.map((a) => (
                  <option key={a.language} value={a.language}>{a.label || a.language}</option>
                ))}
              </select>
            </div>
          )}

          {subtitles.length > 0 && (
            <div className="netflix_extra_group">
              <span className="netflix_extra_label">Legendas</span>
              <select
                className="netflix_extra_select"
                value={selectedSubtitle}
                onChange={(e) => setSelectedSubtitle(e.target.value)}
              >
                <option value="">Desativadas</option>
                {subtitles.map((s) => (
                  <option key={s.language} value={s.language}>{s.label || s.language}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
